<template>
  <div class="other-cryptocurrencies-body">
    <div class="other-cryptocurrencies-wrapper">
      <a
        v-for="(item, index) in updatedList"
        :key="index"
        :href="item.actionLink"
        :title="getMeta(item)"
        class="other-cryptocurrencies-block"
      >
        <div class="other-cryptocurrencies-block__wrapper">
          <img
            class="other-cryptocurrencies-block__icon"
            :src="
              getPaybisIconsUrl(
                `/colored/crypto-icons/${item.assetCode.toUpperCase()}.svg`
              )
            "
            width="64"
            height="64"
            loading="lazy"
            :alt="item.assetName"
          />

          <div class="other-cryptocurrencies-info">
            <span
              class="other-cryptocurrencies-info__percentage"
              :class="
                item.changePercent24h < 0
                  ? 'other-cryptocurrencies-info__percentage--negative'
                  : 'other-cryptocurrencies-info__percentage--positive'
              "
            >
              {{
                (item.changePercent24h > 0 ? "+" : "") + item.changePercent24h
              }}%
            </span>
            <small class="other-cryptocurrencies-info__price">
              <transition name="fade">
                <span :key="formatNumberWithDecimals(item.price)">
                  ${{ formatNumberWithDecimals(item.price) }}
                </span>
              </transition>
            </small>
          </div>
        </div>
        <h4 class="other-cryptocurrencies-block__title">
          {{ getActionText(item) }}
        </h4>
        <small class="other-cryptocurrencies-block__subtitle">
          {{ getPaymentText(item) }}
        </small>
      </a>
    </div>
    <button
      v-if="!showFullList"
      @click="handleFullList"
      class="button button--secondary button--small other-cryptocurrencies-button"
    >
      {{ i18nInstanse.t("kano.other-cryptocurrencies.button") }}
    </button>
  </div>
</template>
<script setup lang="ts">
import { ref, onBeforeMount, computed } from "vue";
import { OtherCryptocurrenciesList } from "./types";
import { i18n as i18nInstanse } from "@kano/i18n";
import { getPaybisIconsUrl } from "@/helpers/cdnHelper";
import { formatNumberWithDecimals } from "@/helpers/numberFormatter";
import ws from '@/plugins/cryptoPriceWS';
import store from '@/store/otherCryptoCurrency';
import { useBotDetect } from '@paybis/frontend-common-lib/src/composables/useBotDetect';

const FIAT = 'USD';
const MARKET = 'CCCAGG';

const props = defineProps<{
  list: OtherCryptocurrenciesList[];
}>();

const showFullList = ref(false);

onBeforeMount (() => {
  const { connect } = ws('otherCryptoCurrency', MARKET, FIAT);
  const { isBot } = useBotDetect();
  if (!isBot) {
    setTimeout(connect, 15_000);
  }
  store.dispatch('otherCryptoCurrency/updateList', props.list);
});

const updatedList =  computed(() => store.getters['otherCryptoCurrency/list']);

const handleFullList = () => {
  showFullList.value = true;
  const block = document.querySelector(".other-cryptocurrencies");
  if (block) {
    block.classList.add("other-cryptocurrencies--full-list");
  }
};

const getActionText = (item: OtherCryptocurrenciesList) => {
  return item.flow === "buy"
    ? i18nInstanse.t("kano.other-cryptocurrencies.buy-crypto", {
        cryptoName: item.assetName,
      })
    : i18nInstanse.t("kano.other-cryptocurrencies.sell-crypto", {
        cryptoName: item.assetName,
      });
};

const getPaymentText = (item: OtherCryptocurrenciesList) => {
  return i18nInstanse.t("kano.other-cryptocurrencies.payment-method-info", {
    paymentMethodName: item.payMethodName,
  });
};

const getMeta = (item: OtherCryptocurrenciesList) => {
  return `${getActionText(item)} ${getPaymentText(item)} ${i18nInstanse.t(
    "kano.other-cryptocurrencies.meta-info"
  )}`;
};
</script>
<style lang="scss" scoped>
.fade-enter-active{
  transition: opacity 1s;
}
.fade-enter {
  opacity: .2;
}
.fade-leave-active {
  display: none;
}
</style>